<template>
  <div>
    <CCard class="bg-style2">
      <CCardBody>
        <CRow>
          <CCol>
            <CDataTable
              :hover="true"
              :bordered="true"
              :small="true"
              :fixed="true"
              :fields="fields"
              :items="objs.allCars"
              :items-per-page="20"
              pagination
            >
              <template #index="{ index }">
                <td>{{ index + 1 }}</td>
              </template>

              <template #reportDescription="{ item }">
                <td>{{ item.reportDescription }}</td>
              </template>

              <template #plate="{ item }">
                <td>{{ item.plate }}</td>
              </template>

              <template #carType="{ item }">
                <td>{{ item.carType }}</td>
              </template>

              <template #carRequestStatus="{ item }">
                <td>{{ item.carRequestStatus }}</td>
              </template>

              <template #actions="{ item }">
                <td class="text-center">
                  <CButton
                    color="success"
                    size="sm"
                    shape="pill"
                    variant="outline"
                    class="mr-1"
                    @click="approveCar(item)"
                    :disabled="item.carRequestStatus !== 'pending'"
                  >
                    <CIcon name="cil-check" />
                  </CButton>

                  <CButton
                    color="danger"
                    size="sm"
                    shape="pill"
                    variant="outline"
                    @click="rejectCar(item)"
                    :disabled="item.carRequestStatus !== 'pending'"
                  >
                    <CIcon name="cil-x" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "CarsRequest",
  data() {
    return {
      objs: { allCars: [] },
      fields: [
        { key: "index", label: "#", _style: "width:50px; text-align:center" },
        { key: "reportDescription", label: "รายละเอียดคำขอ" },
        { key: "plate", label: "ป้ายทะเบียน" },
        { key: "carType", label: "รุ่น/ประเภทรถ" },
        { key: "carRequestStatus", label: "สถานะคำขอ" },
        { key: "actions", label: "การจัดการ", _style: "width:150px; text-align:center" },
      ],
    };
  },

  created() {
    this.$store.dispatch("Dashboard/reports", {});
  },

  computed: {
    ...mapGetters({ reports: "Dashboard/reports" }),
  },

  watch: {
    reports: {
      immediate: true,
      handler(value) {
        if (!value || !value.length) {
          this.objs.allCars = [];
          return;
        }

        const flattened = value.flatMap(report => {
          const cars = report.cars || report.car || [];
          return cars.map(c => ({
            reportId: report._id,
            reportDescription: report.description || "-",
            plate: c.description?.find(d => d.key === "plate")?.value || "-",
            carType: c.title?.find(t => t.key === "th")?.value || "-",
            carRequestStatus: c.request_status || "pending",
            carId: c._id,
            reportDocId: report._id,
          }));
        });

        this.objs.allCars = flattened.filter(c => c.carRequestStatus === "pending");
      },
    },
  },

  methods: {
    async approveCar(item) {
      const { isConfirmed } = await Swal.fire({
        title: "คุณแน่ใจหรือไม่?",
        text: `คุณต้องการอนุมัติรถ ${item.plate} ใช่หรือไม่`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "ใช่, อนุมัติ",
        cancelButtonText: "ยกเลิก",
      });
      if (!isConfirmed) return;

      try {
        // อัปเดตสถานะรถ
        await this.$store.dispatch("Car/updateCar", {
          _id: item.carId,
          request_status: "approved",
          status: "inactive",
        });

        // บันทึก log action
        await this.$store.dispatch("Dashboard2/logCaseAction", {
          caseId: item.reportId,
          by: "ผู้ใช้ระบบ",
          carId: item.carId,
          carAction: "approved",
          reason: `อนุมัติรถ ${item.plate} (${item.carType})`,
        });

        // update local state
        this.objs.allCars = this.objs.allCars.map(c =>
          c.carId === item.carId ? { ...c, carRequestStatus: "approved" } : c
        );

        Swal.fire({
          icon: "success",
          title: "อนุมัติเรียบร้อย",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error(err);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถอนุมัติได้",
        });
      }
    },

    async rejectCar(item) {
      const { isConfirmed } = await Swal.fire({
        title: "คุณแน่ใจหรือไม่?",
        text: `รถ ${item.plate} จะถูกปฏิเสธและลบออกจากรายงาน`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ใช่, ปฏิเสธ",
        cancelButtonText: "ยกเลิก",
      });
      if (!isConfirmed) return;

      try {
        // อัปเดตสถานะรถ
        await this.$store.dispatch("Car/updateCar", {
          _id: item.carId,
          request_status: null,
          status: "active",
        });

        // ลบจาก report
        await this.$store.dispatch("Car/removeCarsFromReport", {
          reportId: item.reportDocId,
          carIds: [item.carId],
        });

        // บันทึก log action
        await this.$store.dispatch("Dashboard2/logCaseAction", {
          caseId: item.reportId,
          by: "ผู้ใช้ระบบ",
          carId: item.carId,
          carAction: "rejected",
          reason: `ปฏิเสธรถ ${item.plate} (${item.carType})`,
        });

        // update local state
        this.objs.allCars = this.objs.allCars.filter(c => c.carId !== item.carId);

        const report = this.reports.find(r => r._id === item.reportDocId);
        if (report) report.cars = report.cars.filter(c => c._id !== item.carId);

        Swal.fire({
          icon: "success",
          title: "ปฏิเสธเรียบร้อย",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error(err);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถปฏิเสธได้",
        });
      }
    },
  },
};
</script>
